import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { fetchRoundPicks } from './api';
import type { MilharTierId } from '../../shared/milhar/tiers';

/**
 * 本期号码分布。视觉规格 §12C。
 *
 * 三条隐私与认知底线：
 * 1. **只有号码和人数，绝不含身份** —— 不可协商
 * 2. **用「人数」不用「金额」** —— 金额会暴露单笔下注规模，且对玩家无用，
 *    他关心的是分几份
 * 3. **顶部那句「不改变概率」不可折叠、不可变灰** —— 羊群效应是这个 sheet
 *    唯一的真实风险：naive 用户会把「12 个人买了 7777」误读成「7777 要出了」
 */
export function RoundPicksSheet({
  myPick,
  token,
  tier = '4',
  onClose,
}: {
  myPick: string | null;
  token?: string | null;
  tier?: MilharTierId;
  onClose: () => void;
}) {
  const t = useI18nStore((state) => state.t);
  const query = useQuery({
    queryKey: ['milhar-round-picks', tier],
    queryFn: () => fetchRoundPicks(token, tier),
    staleTime: 10_000,
  });

  const picks = query.data?.picks || [];

  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="profile-sheet milhar-picks-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={t('milharRoundNumbersTitle')}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="sheet-grabber" />
        <header className="sheet-header">
          <h2>{t('milharRoundNumbersTitle')}</h2>
          <button className="icon-button" type="button" onClick={onClose} aria-label={t('close')}>
            <X size={18} aria-hidden="true" />
          </button>
        </header>

        {/* 不可折叠、不可变灰 —— 见文件头注释第 3 条 */}
        <p className="milhar-picks-disclaimer">{t('milharRoundNumbersHint')}</p>

        <ul className="milhar-picks-list">
          {picks.map((row) => (
            <li
              key={row.pick}
              className={`milhar-picks-row${row.pick === myPick ? ' is-mine' : ''}`}
              ref={row.pick === myPick
                ? (el) => el?.scrollIntoView({ block: 'center' })
                : undefined}
            >
              <b className="milhar-picks-number mono">{row.pick}</b>
              <span className="milhar-picks-people">
                {t('milharPeopleCount').replace('%n%', String(row.people))}
                {row.pick === myPick ? ` · ${t('milharYourNumber')}` : ''}
              </span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
